import { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { useSectorInsightsList } from './useSectorInsights';
import { formatMarketCapKrw } from './format';
import type { SectorInsightsListItem } from './types';

type SortKey =
  | 'canonical_display_name'
  | 'source_sector_count'
  | 'stock_count'
  | 'total_market_cap';
type SortDir = 'asc' | 'desc';

interface Props {
  onRowClick: (canonicalKey: string) => void;
}

export function SectorInsightsList({ onRowClick }: Props): JSX.Element {
  // URL ?q=... 동기화: 초기값은 URL 에서 읽고, 입력 시 300ms debounce 후 URL 과 쿼리에 반영.
  const [searchParams, setSearchParams] = useSearchParams();
  const urlQ = searchParams.get('q')?.trim() ?? '';
  const [inputQ, setInputQ] = useState<string>(urlQ);
  const [debouncedQ, setDebouncedQ] = useState<string>(urlQ);

  // URL → input 동기화 (뒤로가기 등 외부 변경 반영)
  useEffect(() => {
    setInputQ(urlQ);
    setDebouncedQ(urlQ);
  }, [urlQ]);

  // input → debouncedQ (300ms)
  useEffect(() => {
    if (inputQ === debouncedQ) return;
    const t = setTimeout(() => setDebouncedQ(inputQ), 300);
    return () => clearTimeout(t);
  }, [inputQ, debouncedQ]);

  // debouncedQ → URL (replace 로 히스토리 오염 방지)
  useEffect(() => {
    if (debouncedQ === urlQ) return;
    const next = new URLSearchParams(searchParams);
    if (debouncedQ) next.set('q', debouncedQ);
    else next.delete('q');
    setSearchParams(next, { replace: true });
  }, [debouncedQ, urlQ, searchParams, setSearchParams]);

  const { data, isLoading, isError, error } = useSectorInsightsList(debouncedQ);
  const [sortKey, setSortKey] = useState<SortKey>('total_market_cap');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const items = data?.items ?? [];
  const tradingDate = data?.trading_date ?? null;

  const sorted = useMemo(() => {
    const copy = [...items];
    copy.sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      let cmp: number;
      if (typeof av === 'string' && typeof bv === 'string') {
        cmp = av.localeCompare(bv, 'ko-KR');
      } else {
        cmp = (av as number) - (bv as number);
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [items, sortKey, sortDir]);

  const handleSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      // 숫자형은 기본 desc, 문자열은 asc
      setSortDir(key === 'canonical_display_name' ? 'asc' : 'desc');
    }
  };

  return (
    <>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginBottom: 8,
        }}
        data-testid="sector-insights-list-meta"
      >
        <input
          type="search"
          className="pca-input"
          value={inputQ}
          onChange={(e) => setInputQ(e.target.value)}
          placeholder="섹터명 / 원천섹터명 / 종목명 / 심볼"
          aria-label="섹터 검색"
          data-testid="sector-insights-search"
          style={{ flex: '0 0 320px', maxWidth: '50%' }}
        />
        <span className="pca-pagination__spacer" />
        <span className="pca-muted" style={{ fontSize: '0.8rem' }}>
          기준일: {tradingDate ? <code>{tradingDate}</code> : '—'}
        </span>
      </div>
      <div className="pca-table-wrap">
      <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        <table className="pca-table" data-testid="sector-insights-list">
          <thead>
            <tr>
              <SortableTh
                label="최종 섹터"
                sortKey="canonical_display_name"
                activeKey={sortKey}
                dir={sortDir}
                onClick={handleSort}
              />
              <SortableTh
                label="원천 섹터 수"
                sortKey="source_sector_count"
                activeKey={sortKey}
                dir={sortDir}
                onClick={handleSort}
                width={120}
                align="right"
              />
              <SortableTh
                label="총 종목 수"
                sortKey="stock_count"
                activeKey={sortKey}
                dir={sortDir}
                onClick={handleSort}
                width={120}
                align="right"
              />
              <SortableTh
                label="시가총액 (합계)"
                sortKey="total_market_cap"
                activeKey={sortKey}
                dir={sortDir}
                onClick={handleSort}
                width={160}
                align="right"
              />
              <th style={{ width: 320 }}>원천 섹터들</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={5} className="pca-empty">
                  불러오는 중…
                </td>
              </tr>
            )}
            {isError && !isLoading && (
              <tr>
                <td colSpan={5} className="pca-empty">
                  목록 로드 실패: {(error as Error)?.message ?? 'unknown'}
                </td>
              </tr>
            )}
            {!isLoading && !isError && sorted.length === 0 && (
              <tr>
                <td colSpan={5} className="pca-empty">
                  {debouncedQ
                    ? `검색 결과 없음 — '${debouncedQ}' 매칭 canonical 없음`
                    : '표시할 섹터가 없습니다.'}
                </td>
              </tr>
            )}
            {sorted.map((it) => (
              <ListRow key={it.canonical_key} item={it} onClick={onRowClick} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
    </>
  );
}

function ListRow({
  item,
  onClick,
}: {
  item: SectorInsightsListItem;
  onClick: (canonicalKey: string) => void;
}): JSX.Element {
  const sourceText = useMemo(
    () =>
      item.source_sectors
        .map((s) => `${s.source}:${s.sector_name}`)
        .join(', '),
    [item.source_sectors]
  );

  return (
    <tr
      className="si-group-row"
      onClick={() => onClick(item.canonical_key)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick(item.canonical_key);
        }
      }}
    >
      <td>{item.canonical_display_name}</td>
      <td style={{ textAlign: 'right' }}>
        {item.source_sector_count.toLocaleString('ko-KR')}
      </td>
      <td style={{ textAlign: 'right' }}>
        {item.stock_count.toLocaleString('ko-KR')}
      </td>
      <td style={{ textAlign: 'right' }}>
        {formatMarketCapKrw(item.total_market_cap)}
      </td>
      <td
        title={sourceText || '—'}
        style={{
          maxWidth: 320,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {sourceText || <span className="pca-muted">—</span>}
      </td>
    </tr>
  );
}

function SortableTh({
  label,
  sortKey,
  activeKey,
  dir,
  onClick,
  width,
  align,
}: {
  label: string;
  sortKey: SortKey;
  activeKey: SortKey;
  dir: SortDir;
  onClick: (k: SortKey) => void;
  width?: number;
  align?: 'left' | 'right';
}): JSX.Element {
  const active = sortKey === activeKey;
  return (
    <th
      style={{
        width,
        cursor: 'pointer',
        userSelect: 'none',
        textAlign: align ?? 'left',
      }}
      onClick={() => onClick(sortKey)}
      aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        {label}
        {active &&
          (dir === 'asc' ? (
            <ChevronUp size={12} strokeWidth={1.75} aria-hidden />
          ) : (
            <ChevronDown size={12} strokeWidth={1.75} aria-hidden />
          ))}
      </span>
    </th>
  );
}
